import { useEffect, useState } from 'react';

import type { Id, QltNamedEntity, QltViolation } from '../../api/types';
import { Toast } from '../../components';
import { useQltSchoolSettingsStore, useQltSlotAttendanceStore, useQltViolationStore } from '../../stores/qlt';
import { conductGate } from './conduct-state';
import { parseDescription } from './conductEntry';
import { ViolationForm, type ViolationFormValue } from './ViolationForm';
import '../../styles/class-management.css';

function entityId(value: Id | QltNamedEntity | undefined): Id | null {
  if (value === undefined) return null;
  return typeof value === 'object' ? value.id : value;
}

function formatTime(value?: string): string {
  if (!value) return '';
  const date = new Date(value);
  return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
}

function studentName(item: QltViolation): string {
  if (typeof item.student !== 'object') return 'Học sinh';
  return item.student.name || item.student.code || 'Học sinh';
}

// every ticked reason on its own line, the note last; a one-line ellipsis hid all but the first
function ConductLines({ name, description }: { name: string; description: string }) {
  const lines = parseDescription(description);
  return (
    <span className="cm-conduct__text">
      <strong>{name}</strong>
      {lines.reasons.map((reason, index) => <span className="cm-conduct__reason" key={index}>{reason}</span>)}
      {lines.note ? <span className="cm-conduct__reason cm-conduct__note">Ghi chú: {lines.note}</span> : null}
    </span>
  );
}

export interface ConductCardProps {
  classNoteId?: Id;
  classId: string;
  classTitle: string;
  subjectId?: Id;
  date: string;
  periodLabel?: string;
  subjectTitle?: string;
}

export function ConductCard({ classNoteId, classId, classTitle, subjectId, date, periodLabel, subjectTitle }: ConductCardProps) {
  const violations = useQltViolationStore();
  const schoolSettings = useQltSchoolSettingsStore();
  const students = useQltSlotAttendanceStore((state) => state.rows);
  const rosterLoading = useQltSlotAttendanceStore((state) => state.loading);
  const rosterError = useQltSlotAttendanceStore((state) => state.error);
  const rosterContext = useQltSlotAttendanceStore((state) => state.context);
  const loadRoster = useQltSlotAttendanceStore((state) => state.load);
  const [formOpen, setFormOpen] = useState(false);
  const [savedToast, setSavedToast] = useState(false);

  // no class note means no records yet; never keep another period's list on screen
  useEffect(() => {
    if (!classNoteId) {
      violations.reset();
      return;
    }
    void violations.loadForClassNote(classNoteId);
  }, [classNoteId, violations.loadForClassNote, violations.reset]);

  useEffect(() => {
    if (!schoolSettings.settings) void schoolSettings.load();
  }, [schoolSettings.settings, schoolSettings.load]);

  async function create(value: ViolationFormValue) {
    const semester = entityId(schoolSettings.settings?.currentSemester);
    if (!classNoteId || !subjectId || semester === null) return;
    const item = await violations.create({ ...value, class: classId, subject: subjectId, semester, classNote: classNoteId, date: `${date.slice(0, 10)}T00:00:00.000+07:00` });
    if (item) {
      setFormOpen(false);
      setSavedToast(true);
    }
  }

  const gate = conductGate({
    classNoteId,
    subjectId,
    semester: entityId(schoolSettings.settings?.currentSemester),
    roster: { count: students.length, loading: rosterLoading, error: rosterError, requested: rosterContext !== null },
  });
  const blocked = gate.reason;

  return (
    <>
      <section className="cm-card">
        <div className="cm-card__head">
          <h3 className="cm-card__title">Khen thưởng - kỉ luật</h3>
          <button type="button" className="cm-card__more" disabled={Boolean(blocked)} title={blocked || undefined} onClick={() => setFormOpen(true)}>Thêm</button>
        </div>
        {blocked ? (
          <p className={gate.retry ? 'cm-error' : 'cm-card__empty'} role={gate.retry ? 'alert' : undefined}>
            {blocked}
            {gate.retry && rosterContext ? <button type="button" className="cm-card__more" onClick={() => void loadRoster(rosterContext)}>Thử lại</button> : null}
          </p>
        ) : null}
        {!blocked && violations.items.length === 0 ? <p className="cm-card__empty">Chưa có ghi nhận trong tiết học.</p> : null}
        {violations.items.length > 0 ? (
          <ul className="cm-conduct-list">
            {violations.items.map((item) => (
              <li key={String(item.id || item._id)}>
                <span className={`cm-conduct__badge cm-conduct__badge--${item.type === 'commendation' ? 'commendation' : 'violation'}`} aria-hidden={true}>{item.type === 'commendation' ? 'KT' : 'VP'}</span>
                <ConductLines name={studentName(item)} description={item.description} />
                <time className="cm-conduct__time">{formatTime(item.createdAt)}</time>
              </li>
            ))}
          </ul>
        ) : null}
        {schoolSettings.error ? <p className="cm-error" role="alert">{schoolSettings.error}</p> : null}
      </section>
      <ViolationForm open={formOpen} classTitle={classTitle} periodLabel={periodLabel} subjectTitle={subjectTitle} initialType="commendation" students={students} saving={violations.loading} error={violations.error} onClose={() => setFormOpen(false)} onSubmit={(value) => void create(value)} />
      <Toast open={savedToast} message="Đã lưu ghi nhận" onClose={() => setSavedToast(false)} />
    </>
  );
}
